import React, { useEffect, useMemo, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { FieldError, useFieldArray, useForm } from 'react-hook-form'
import {
  CreateSupplierDto,
  SupplierContactType,
  SupplierDto,
  SupplierPaymentMethod,
  SupplierType,
} from '@services/dtos'
import { supplierSchema } from '@utils/schemas'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import { Autocomplete, Button, Chip, FormControlLabel, FormHelperText, Grid, IconButton, Switch, Tab, Tabs, TextField, Typography } from '@mui/material'
import {
  createSupplier,
  updateSupplier,
} from '@state/reducers/supplierReducer'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import InputField from '../inputs/InputField'
import { FormCrudHeader } from './FormCrudHeader'
import { bankFields, SupplierContactTypeLabels, SupplierPaymentMethodLabels, SupplierTypeLabels } from '@utils/constants'
import SelectField from '../inputs/SelectField'
import { Delete as DeleteIcon } from '@mui/icons-material'
import ErrorIcon from '@mui/icons-material/Error'
import { Site } from '@services/api'

const TYPE_OPTIONS = (
  Object.entries(SupplierTypeLabels) as Array<[SupplierType, string]>
).map(([value, label]) => ({ label, value }))
const CONTACT_TYPE_OPTIONS = (
  Object.entries(SupplierContactTypeLabels) as Array<[SupplierContactType, string]>
).map(([value, label]) => ({ label, value }))
const PAYMENT_METHOD_OPTIONS = (
  Object.entries(SupplierPaymentMethodLabels) as Array<[SupplierPaymentMethod, string]>
).map(([value, label]) => ({ label, value }))

enum Section {
  Info,
  Payment,
}

type Props = {
  editingSupplier?: SupplierDto | null
  highlightProductionSites?: boolean
  defaultValues?: Partial<CreateSupplierDto>
  onClose: () => void
}

const SupplierForm: React.FC<Props> = ({
  highlightProductionSites,
  editingSupplier,
  onClose,
  defaultValues
}) => {
  const {
    register,
    setValue,
    handleSubmit,
    reset,
    control,
    watch,
    formState: { errors },
  } = useForm<CreateSupplierDto>({
    defaultValues: editingSupplier
      ? {
          name: editingSupplier.name,
          siret: editingSupplier.siret,
          type: editingSupplier.type,
          address: editingSupplier.address,
          notes: editingSupplier.notes,
          contacts: editingSupplier.contacts.map(contact => ({
            name: contact.name || '',
            email: contact.email || '',
            phone: contact.phone || '',
            type: contact.type,
          })),
          paymentMethods: editingSupplier.paymentMethods,
          countryCode: editingSupplier.countryCode,
          bankCode: editingSupplier.bankCode,
          branchCode: editingSupplier.branchCode,
          accountNumber: editingSupplier.accountNumber,
          controlKey: editingSupplier.controlKey,
          swiftCode: editingSupplier.swiftCode,
          cyclotron: highlightProductionSites ?? editingSupplier.cyclotron,
          productionSites: editingSupplier.productionSites,
          customerSites: editingSupplier.customerSites.map(({ customerNumber, site }) => ({
            customerNumber,
            siteId: site.id,
          })),
        }
      : defaultValues,
    resolver: yupResolver(supplierSchema),
  })
  const [section, setSection] = useState<Section>(Section.Info)
  const handleChangeSection = (_: React.SyntheticEvent, newSection: Section) => {
    setSection(newSection)
  }
  const { user } = useAppSelector(({ auth }) => ({
    user: auth.user,
  }))
  const sites = useMemo(() => {
    return (user as any & { sites: Site[] })?.sites
  }, [user])
  const { fields: contactFields, append: appendContact, remove: removeContact } = useFieldArray({
    control,
    name: 'contacts',
  })
  const { fields: customerSiteFields, append: appendCustomerSite, remove: removeCustomerSite } = useFieldArray({
    control,
    name: 'customerSites',
  })

  const dispatch = useAppDispatch()

  const [isSubmitting, setIsSubmitting] = useState(false)

  const cb = async (msg: string) => {
    dispatch(
      enqueueSnackbar({
        message: msg,
        options: { variant: 'success' },
      }),
    )
  }

  useEffect(() => {
    return () => {
      reset()
    }
  }, [dispatch])
  
  const onSubmitForm = async (data: CreateSupplierDto) => {
    setIsSubmitting(true)

    if (editingSupplier) {
      dispatch(updateSupplier({ id: editingSupplier.id, dto: data }))
        .unwrap()
        .then(() => {
          cb('Le fournisseur a été modifié avec succès')
          onClose()
        })
        .catch(() => cb('Une erreur est survenue'))
        .finally(() => setIsSubmitting(false))
    } else {
      dispatch(createSupplier(data))
        .unwrap()
        .then(() => {
          cb('Le fournisseur a été créé avec succès')
          onClose()
        })
        .catch(() => cb('Une erreur est survenue'))
        .finally(() => setIsSubmitting(false))
    }
  }

  const infoFields = [
    { name: 'name', label: 'Nom', sm: 12 },
    { name: 'siret', label: 'SIRET' },
    { name: 'address', label: 'Adresse', sm: 12 },
    { name: 'notes', label: 'Notes', type: 'textarea', sm: 12 },
  ]

  const renderFields = (fields: { name: string; label: string; type?: string; sm?: number }[]) => {
    return fields.map((field) => (
      <Grid item xs={12} sm={field.sm ? field.sm : 6} key={field.name}>
        <InputField
          control={control}
          name={field.name as keyof CreateSupplierDto}
          label={field.label}
          type={field.type ? field.type : 'text'}
          multiline={field.type === 'textarea'}
          rows={field.type === 'textarea' ? 2 : undefined}
        />
      </Grid>
    ))
  }

  const errorSectionInfo = ['type', 'contacts', 'customerSites', 'cyclotron', ...infoFields.map(({ name }) => name)].some(field => !!errors[field])
  const errorSectionPayment = [...bankFields.map(({ name }) => name), 'paymentMethods'].some(field => !!errors[field])

  return (
    <div className="flex w-full h-full p-4">
      <form 
        onSubmit={(e) => {
          e.stopPropagation()
          handleSubmit(onSubmitForm)(e)
        }}
        className="w-full"
      >
        <FormCrudHeader
          isSubmitting={isSubmitting}
          onClose={onClose}
          editing={!!editingSupplier}
        />

        <div className="max-w-screen-md mx-auto mt-4">
          <fieldset className="border border-gray-200 p-4 rounded-lg">
            <legend className="text-lg font-bold border border-gray-200 p-2 rounded-lg">
              Fournisseur
            </legend>

            <Tabs
              value={section}
              onChange={handleChangeSection}
              variant="scrollable"
              scrollButtons
              allowScrollButtonsMobile
              className="mb-4 [&_.MuiTabs-flexContainer]:justify-center"
            >
              <Tab label="Informations" icon={errorSectionInfo ? <ErrorIcon /> : undefined} iconPosition='start' />
              <Tab label="Paiement" icon={errorSectionPayment ? <ErrorIcon /> : undefined} iconPosition='start' />
            </Tabs>

            <Grid container rowSpacing={2} columnSpacing={4} mt={1}>
              {section === Section.Info && (<>
                {renderFields([infoFields[0], infoFields[1]])}

                <Grid item xs={12} sm={6}>
                  <SelectField
                    control={control}
                    name="type"
                    options={TYPE_OPTIONS}
                    value={watch('type')}
                    label="Type"
                  />
                </Grid>

                <Grid item sm={12} md={3}>
                  <FormControlLabel control={               
                    <Switch
                      {...register('cyclotron')}
                      defaultChecked={highlightProductionSites ? true : (editingSupplier?.cyclotron ?? false)}
                      color="primary"
                    />}
                    label="Cyclotron"
                    labelPlacement='start' />
                </Grid>

                {watch('cyclotron') && (<Grid item sm={12} md={9}>
                  <Autocomplete
                    style={highlightProductionSites ? { border: '1.5px solid #3A0CA1', borderRadius: 6 } : {}}
                    multiple
                    freeSolo
                    options={[]}
                    value={watch('productionSites')}
                    onChange={(event, newValue) => setValue('productionSites', newValue)}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index })
                        return (
                          <Chip
                            key={key}
                            variant="outlined"
                            label={option}
                            {...tagProps}
                          />
                        )
                      })
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Lieux production" placeholder='Saisissez un lieu et appuyez sur "Entrer ↵"' />
                    )}
                  />
                </Grid>)}

                {renderFields([infoFields[2]])}

                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      appendContact({ email: '', phone: '', name: '', type: SupplierContactType.COMMERCIAL })
                    }}
                  >
                    Ajouter un contact
                  </Button>
                </Grid>
                {contactFields.map((_field, index) => (
                  <Grid item xs={12} key={`contact-${index}`}>
                  <fieldset className="border border-gray-200 p-2 rounded-lg">
                    <legend className="text-xs text-gray-500 font-medium mb-2 border border-gray-200 pl-3 rounded-lg">
                      Contact {index + 1}
                      <IconButton
                        onClick={() => removeContact(index)}
                        size='small'>
                        <DeleteIcon />
                      </IconButton>
                    </legend>
                    <Grid container rowSpacing={2} columnSpacing={4}>
                      {(errors.contacts?.[index] as unknown as FieldError)?.message && (
                        <Grid item xs={12}>
                          <FormHelperText error>
                            {(errors.contacts?.[index] as unknown as FieldError)?.message}
                          </FormHelperText>
                        </Grid>
                      )}
                      {renderFields([{ name: `contacts.${index}.name`, label: 'Nom' }])}
                      {renderFields([{ name: `contacts.${index}.phone`, label: 'Téléphone' }])}
                      {renderFields([{ name: `contacts.${index}.email`, label: 'Email' }])}
                      <Grid item xs={12} sm={6}>
                        <SelectField
                          control={control}
                          name={`contacts.${index}.type`}
                          options={CONTACT_TYPE_OPTIONS}
                          value={watch(`contacts.${index}.type`)}
                          label="Service"
                        />
                      </Grid>
                    </Grid>
                  </fieldset>
                  </Grid>
                ))}

                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      appendCustomerSite({ customerNumber: '', siteId: sites[0]?.id || undefined })
                    }}
                    disabled={!sites.length || (watch('customerSites') ?? []).length === sites.length}
                  >
                    Ajouter un numéro client
                  </Button>
                </Grid>
                {customerSiteFields.map((_field, index) => (
                  <Grid item xs={12} key={`customer-site-${index}`}>
                  <fieldset className="border border-gray-200 p-2 rounded-lg">
                    <legend className="text-xs text-gray-500 font-medium mb-2 border border-gray-200 pl-3 rounded-lg">
                      N° Client {index + 1}
                      <IconButton
                        onClick={() => removeCustomerSite(index)}
                        size='small'>
                        <DeleteIcon />
                      </IconButton>
                    </legend>
                    <Grid container rowSpacing={2} columnSpacing={4}>
                      {(errors.customerSites?.[index] as unknown as FieldError)?.message && (
                        <Grid item xs={12}>
                          <FormHelperText error>
                            {(errors.customerSites?.[index] as unknown as FieldError)?.message}
                          </FormHelperText>
                        </Grid>
                      )}
                      {renderFields([{ name: `customerSites.${index}.customerNumber`, label: 'N° client' }])}
                      <Grid item xs={12} sm={6}>
                        <SelectField
                          control={control}
                          name={`customerSites.${index}.siteId`}
                          options={sites
                            .filter(site => !customerSiteFields.some(({ siteId }) => siteId === site.id) || site.id === watch(`customerSites.${index}.siteId`))
                            .map(({ id, label }) => ({ label, value: id }))}
                          value={watch(`customerSites.${index}.siteId`)}
                          label="Site"
                        />
                      </Grid>
                    </Grid>
                  </fieldset>
                  </Grid>
                ))}

                <Grid item xs={12}>
                  {renderFields([infoFields[3]])}
                </Grid>
              </>)}

              {section === Section.Payment && (<>
                <Grid item xs={12}>
                  <SelectField
                    control={control}
                    name="paymentMethods"
                    options={PAYMENT_METHOD_OPTIONS}
                    value={watch('paymentMethods') || []}
                    multiple
                    label="Méthodes paiement"
                  />
                </Grid>

                <Grid item xs={12}><Typography variant="subtitle2">Informations bancaires</Typography></Grid>
                {renderFields(bankFields)}
              </>)}
            </Grid>
          </fieldset>
        </div>
      </form>
    </div>
  )
}

export default SupplierForm
